import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../ui/sheet';
import { stripHtml } from '../descriptionUtils';

const ALLOWED_TAGS = [
    'p',
    'br',
    'h1',
    'h2',
    'h3',
    'ul',
    'ol',
    'li',
    'strong',
    'em',
    'u',
    's',
    'a',
];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

interface FolderDescriptionProps {
    title: string;
    description?: string;
}

const MAX_PREVIEW_CHARS = 280;

export const FolderDescription = ({
    title,
    description,
}: FolderDescriptionProps) => {
    const { t } = useTranslation();
    const [sheetOpen, setSheetOpen] = useState(false);

    const preview = stripHtml(description);
    const sanitizedHtml = useMemo(
        () =>
            description
                ? DOMPurify.sanitize(description, {
                      ALLOWED_TAGS,
                      ALLOWED_ATTR,
                  })
                : '',
        [description]
    );
    if (!preview) return null;

    const truncated = preview.length > MAX_PREVIEW_CHARS;
    const visiblePreview = truncated
        ? preview.slice(0, MAX_PREVIEW_CHARS).trimEnd()
        : preview;

    return (
        <>
            <p className="mt-4 max-w-[70ch] text-sm leading-6 text-zinc-300">
                {visiblePreview}
                {truncated && (
                    <>
                        {'… '}
                        <button
                            type="button"
                            onClick={() => setSheetOpen(true)}
                            className="font-medium text-blue hover:underline"
                        >
                            {t('folders.detail.description.viewMore')}
                        </button>
                    </>
                )}
            </p>

            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetContent
                    side="right"
                    className="flex w-full flex-col p-0 sm:max-w-xl"
                >
                    <SheetHeader className="border-b border-subtle px-6 py-5">
                        <SheetTitle className="pr-8">{title}</SheetTitle>
                    </SheetHeader>
                    {sanitizedHtml && (
                        <div className="flex-1 overflow-y-auto px-6 py-5">
                            <div
                                className="mx-auto max-w-[70ch] text-sm leading-7 text-zinc-300 [&_h1]:mb-3 [&_h1]:mt-5 [&_h1]:text-xl [&_h1]:font-medium [&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-lg [&_h2]:font-medium [&_li]:mt-1.5 [&_ol]:ml-5 [&_ol]:list-decimal [&_p]:my-3 [&_ul]:ml-5 [&_ul]:list-disc"
                                dangerouslySetInnerHTML={{
                                    __html: sanitizedHtml,
                                }}
                            />
                        </div>
                    )}
                </SheetContent>
            </Sheet>
        </>
    );
};
